import { useRef } from "react";
import cx from "classnames";
import { faTag } from "@fortawesome/pro-regular-svg-icons";

import { Optional } from "common/utils/types";
import { BasicCatalogImage, BasicCategorySummary } from "common/types";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import { useBreakpoints } from "client/common/hooks";
import { urls } from "client/routes";
import { sendSelectItem } from "client/analytics/utils";

import { IProductLineResultFact } from "../types";

import styles from "../styles/ProductLineCard.module.less";

export type ProductLineForCard = {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  categories: BasicCategorySummary[];
  primary_category_name: string;
  image: Optional<BasicCatalogImage>;
  description: string;
  facts: IProductLineResultFact[];
  is_featured: boolean;
  is_loyalty_program_featured?: boolean;
  patient_prescription_label: Optional<string>;
  promotion_label?: string;
};

type StandardProductLineCardProps = { productLine: ProductLineForCard; eventViewName: string };

const StandardProductLineCard = ({
  productLine,
  eventViewName = "Product Line Card",
}: StandardProductLineCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const { isPhoneAndSmaller } = useBreakpoints();
  const hasPrescriptionLabel = Boolean(productLine.patient_prescription_label);
  const isFeatured = productLine.is_loyalty_program_featured || productLine.is_featured;

  return (
    <LinkButton
      className={styles.standardProductLineCard}
      key={productLine.name}
      dest={urls.products.detail({ params: { id: productLine.id } })}
      onClick={() => sendSelectItem(productLine, eventViewName)}
    >
      <div
        className={cx(
          styles.background,
          hasPrescriptionLabel && styles.prescribed,
          !hasPrescriptionLabel && isFeatured && styles.featured,
        )}
        ref={cardRef}
      >
        <div className={styles.content}>
          <div className={styles.imageContainer}>
            <CatalogImage
              image={productLine.image}
              size="medium"
              alt="Product"
              className={styles.image}
            />
            {productLine.promotion_label && (
              <div className={styles.promotion}>
                <Icon icon={faTag} /> {productLine.promotion_label}
              </div>
            )}
          </div>
          <div className={styles.names}>
            {(hasPrescriptionLabel || isFeatured) &&
              (hasPrescriptionLabel ? (
                <div className={styles.chip}>
                  <TextChip variant="info" size={isPhoneAndSmaller ? "s" : "m"}>
                    {productLine.patient_prescription_label}
                  </TextChip>
                </div>
              ) : (
                <div className={styles.chip}>
                  <TextChip variant="highlight" size={isPhoneAndSmaller ? "s" : "m"}>
                    Your Vet&apos;s Choice
                  </TextChip>
                </div>
              ))}
            <div className={styles.name}>{productLine.name}</div>
            <div className={styles.description}>{productLine.description}</div>
          </div>
        </div>
      </div>
      {productLine.facts.length ? (
        <div className={styles.facts}>
          {productLine.facts?.map((fact) => (
            <div className={styles.fact} key={fact.label}>
              <div className={styles.factLabel}>{fact.label}</div>
              <div className={styles.factValue}>{fact.value}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.noFacts} />
      )}
    </LinkButton>
  );
};

type CompactProductLineCardProps = { productLine: ProductLineForCard; eventViewName: string };

const CompactProductLineCard = ({ productLine, eventViewName }: CompactProductLineCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const hasPrescriptionLabel = Boolean(productLine.patient_prescription_label);
  const isFeatured = productLine.is_loyalty_program_featured || productLine.is_featured;

  return (
    <LinkButton
      className={styles.compactProductLineCard}
      key={productLine.name}
      dest={urls.products.detail({ params: { id: productLine.id } })}
      onClick={() => sendSelectItem(productLine, eventViewName)}
    >
      <div
        className={cx(
          styles.background,
          hasPrescriptionLabel && styles.prescribed,
          !hasPrescriptionLabel && isFeatured && styles.featured,
        )}
        ref={cardRef}
      >
        <div className={styles.content}>
          <CatalogImage
            image={productLine.image}
            size="medium"
            alt="Product"
            className={styles.image}
          />
          <div className={styles.names}>
            {(hasPrescriptionLabel || isFeatured) &&
              (hasPrescriptionLabel ? (
                <div className={styles.chip}>
                  <TextChip variant="info" size="s">
                    {productLine.patient_prescription_label}
                  </TextChip>
                </div>
              ) : (
                <div className={styles.chip}>
                  <TextChip variant="highlight" size="s">
                    Your Vet&apos;s Choice
                  </TextChip>
                </div>
              ))}
            <div className={styles.name}>{productLine.name}</div>
            <div className={styles.description}>{productLine.description}</div>
            {productLine.promotion_label && (
              <div className={styles.promotion}>
                <Icon icon={faTag} /> {productLine.promotion_label}
              </div>
            )}
          </div>
        </div>
      </div>
    </LinkButton>
  );
};

export default Object.assign(StandardProductLineCard, { Compact: CompactProductLineCard });
